import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, X, Check, Tag } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TYPE_TABS = [
  { value: "product", label: "Products" },
  { value: "material", label: "Materials" },
  { value: "service", label: "Services" },
  { value: "all", label: "All" },
];

const empty = {
  name: "",
  description: "",
  default_price: 0,
  type: "product",
  category: "both",
  color: "",
  thickness: "",
  notes: "",
  variations: { colors: [], sizes: [] },
};

/** Compact tag-list editor. Type value, press Enter or comma to add. */
function TagInput({ label, value, onChange, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (value.includes(v)) { setDraft(""); return; }
    onChange([...value, v]);
    setDraft("");
  };
  const remove = (v) => onChange(value.filter((x) => x !== v));
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <div className="flex flex-wrap gap-1.5 mb-1.5 min-h-[8px]">
        {value.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-slate-50 border hairline rounded-sm font-mono">
            {v}
            <button className="text-slate-400 hover:text-red-400" onClick={() => remove(v)}><X size={12} /></button>
          </span>
        ))}
      </div>
      <Input
        data-testid={testid}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); }
          if (e.key === "Backspace" && !draft && value.length) remove(value[value.length - 1]);
        }}
        onBlur={add}
        placeholder={placeholder}
      />
    </div>
  );
}

export default function Catalog() {
  const [list, setList] = useState([]);
  const [tab, setTab] = useState("product");
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/products").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  // When user switches tab, reset form to default type for that tab
  useEffect(() => {
    if (!editingId && tab !== "all") setForm((f) => ({ ...f, type: tab }));
  }, [tab, editingId]);

  const shown = useMemo(() => (tab === "all" ? list : list.filter((p) => (p.type || "product") === tab)), [list, tab]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    const payload = {
      ...form,
      default_price: Number(form.default_price) || 0,
      variations: {
        colors: form.variations?.colors || [],
        sizes: form.variations?.sizes || [],
      },
    };
    try {
      if (editingId) {
        await api.put(`/products/${editingId}`, payload); toast.success("Updated");
      } else {
        await api.post("/products", payload); toast.success("Added to catalog");
      }
      setForm({ ...empty, type: tab === "all" ? "product" : tab });
      setEditingId(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      description: p.description || "",
      default_price: p.default_price || 0,
      type: p.type || "product",
      category: p.category || "both",
      color: p.color || "",
      thickness: p.thickness || "",
      notes: p.notes || "",
      variations: {
        colors: p.variations?.colors || [],
        sizes: p.variations?.sizes || [],
      },
    });
  };
  const cancelEdit = () => { setEditingId(null); setForm({ ...empty, type: tab === "all" ? "product" : tab }); };

  const remove = async (id) => {
    if (!window.confirm("Delete this catalog item?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const isMaterial = form.type === "material";
  const showVariations = form.type === "product";

  return (
    <div>
      <PageHeader
        title="Catalog"
        subtitle={`${list.length} items · reusable across every invoice & work order`}
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="bg-transparent border hairline p-1 rounded-sm h-auto">
          {TYPE_TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              data-testid={`catalog-tab-${t.value}`}
              className="rounded-sm text-xs uppercase tracking-wider font-mono data-[state=active]:bg-emerald-700 data-[state=active]:text-white"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left form */}
        <div className="surface p-5">
          <div className="overline mb-3">{editingId ? "Edit Item" : "Add to Catalog"}</div>
          <div className="flex flex-col gap-3">
            <div>
              <Label className="text-xs text-slate-600">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="catalog-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="product">Product</SelectItem>
                  <SelectItem value="material">Material</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Name *</Label>
              <Input
                data-testid="catalog-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={isMaterial ? "e.g. Baltic Birch Plywood" : "e.g. 20oz Tumbler Engrave"}
              />
            </div>

            {!isMaterial && (
              <div>
                <Label className="text-xs text-slate-600">Default Price ($)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  data-testid="catalog-price-input"
                  value={form.default_price}
                  onChange={(e) => setForm({ ...form, default_price: e.target.value })}
                />
              </div>
            )}

            <div>
              <Label className="text-xs text-slate-600">Description</Label>
              <Textarea
                data-testid="catalog-desc-input"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={isMaterial ? "Optional notes about this material" : "Optional item description"}
              />
            </div>

            {isMaterial && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-slate-600">Color</Label>
                    <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="Natural" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Thickness</Label>
                    <Input value={form.thickness} onChange={(e) => setForm({ ...form, thickness: e.target.value })} placeholder='1/8"' />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Best For</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="catalog-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="laser">Laser only</SelectItem>
                      <SelectItem value="uv">UV only</SelectItem>
                      <SelectItem value="both">Both Laser & UV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {showVariations && (
              <div className="border-t hairline pt-3">
                <div className="overline mb-2 flex items-center gap-2"><Tag size={12} /> Variations (optional)</div>
                <div className="flex flex-col gap-3">
                  <TagInput
                    label="Colors"
                    testid="catalog-colors-input"
                    placeholder="Type a color, press Enter"
                    value={form.variations?.colors || []}
                    onChange={(v) => setForm({ ...form, variations: { ...form.variations, colors: v } })}
                  />
                  <TagInput
                    label="Sizes"
                    testid="catalog-sizes-input"
                    placeholder='e.g. 20oz, 30oz'
                    value={form.variations?.sizes || []}
                    onChange={(v) => setForm({ ...form, variations: { ...form.variations, sizes: v } })}
                  />
                  <p className="text-[11px] text-slate-400">
                    Employees can pick a variation when adding this product to an invoice.
                  </p>
                </div>
              </div>
            )}

            <div className="flex gap-2 mt-1">
              <button className="btn-primary flex-1 flex items-center justify-center gap-2" onClick={save} data-testid="save-catalog-btn">
                {editingId ? <><Check size={16} /> Update</> : <><Plus size={16} /> Add</>}
              </button>
              {editingId && (
                <button className="btn-ghost" onClick={cancelEdit}><X size={16} /></button>
              )}
            </div>
          </div>
        </div>

        {/* Right list */}
        <div className="lg:col-span-2 surface overflow-x-auto" data-testid="catalog-list">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Details</th>
                <th className="text-right">Price</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={5} className="text-center text-slate-400 py-10">
                  Nothing in this catalog view yet. Add reusable items here so they auto-fill on invoices & work orders.
                </td></tr>
              )}
              {shown.map((p) => {
                const t = p.type || "product";
                const detail = t === "material"
                  ? [p.color, p.thickness, p.category].filter(Boolean).join(" · ") || "—"
                  : [
                      p.description ? p.description.slice(0, 60) : "",
                      p.variations?.colors?.length ? `${p.variations.colors.length} colors` : "",
                      p.variations?.sizes?.length ? `${p.variations.sizes.length} sizes` : "",
                    ].filter(Boolean).join(" · ") || "—";
                return (
                  <tr key={p.id} className="hover:bg-transparent cursor-default" data-testid={`catalog-row-${p.id}`}>
                    <td className="text-slate-900 font-medium">{p.name}</td>
                    <td><span className="chip" style={{ borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.65)' }}>{t}</span></td>
                    <td className="text-slate-600 text-sm">{detail}</td>
                    <td className="text-right font-mono">{t === "material" ? "—" : money(p.default_price)}</td>
                    <td className="text-right">
                      <div className="flex justify-end gap-2">
                        <button className="text-slate-400 hover:text-slate-900 p-1" onClick={() => startEdit(p)} data-testid={`edit-catalog-${p.id}`}>
                          <PencilSimple size={16} />
                        </button>
                        <button className="text-slate-400 hover:text-red-400 p-1" onClick={() => remove(p.id)} data-testid={`delete-catalog-${p.id}`}>
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
